/* The Record evidence form (designs/products-pdp.md PP4 + UI "Record evidence
   drawer (Screen 4)"; the Data feed arm is kpi-feed-seam.md's "Evidence form,
   Kind: Data feed"). One form, three kinds; the kind chosen changes only the
   fields below it. Kind and Confidence are NATIVE radio groups styled as
   segments (arrow keys and focus come free — UI/UX review must #3). The PP4
   refusals are said here before the round trip (AA2); the server's own
   refusal arrives through `error` and shows verbatim. Pure: the owner wraps
   it in `<aside id="evidence-drawer" role="dialog" aria-labelledby=
   "evidence-title">`, owns the PUT, and returns focus to the row's button. */
import { useEffect, useRef, useState, type JSX, type KeyboardEvent } from "react";
import { feedRefReason, type FeedSourceInfo } from "../../../../shared/feeds";
import type { PdpCriterion, PdpPhase, PdpTarget } from "../../../../shared/pdp";
import { ESTIMATE_CAP_REFUSAL, ESTIMATE_MAX, evidenceReason } from "../../../../shared/product-pdp";
import { NO_FEED_SOURCES, type EvidenceInput, type EvidenceValue } from "./model";

export interface EvidenceSlotInfo {
  name: string;
  swimlane: string;
  path: string;
  phase: PdpPhase;
  /* resolved by the caller from the catalog's role ids */
  ownerRoleNames: string[];
  reviewerRoleNames: string[];
}

export interface EvidenceFormProps {
  slot: EvidenceSlotInfo;
  /* the slot's current value — prefills the form and shows Clear… */
  current: EvidenceValue | null;
  /* GET /api/feeds/sources; [] = the feed arm is refused client-side */
  feedSources: FeedSourceInfo[];
  /* the catalog's criteria; the arm offers the slot's phase's targeted ones */
  criteria: PdpCriterion[];
  /* the server's refusal, verbatim (PP4), or the network sentence (PP18) */
  error: string | null;
  /* bumped by the owner on every refusal, so the SAME sentence twice re-focuses */
  errorNonce?: number;
  saving?: boolean;
  onSave: (input: EvidenceInput) => void;
  onClear: () => void;
  onCancel: () => void;
}

type Kind = EvidenceInput["kind"];

const URL_MAX = 2000;
const TITLE_MAX = 200;
const NOTE_MAX = 300;

/* PP4 as the client says it — the SAME shared rule the route runs, plus the
   one client-only refusal: the feed arm with no configured source. The
   history is empty here because a clear is the drawer's button, not an arm. */
export function evidenceFormReason(input: EvidenceInput, sources: readonly FeedSourceInfo[]): string | null {
  if (input.kind === "feed" && sources.length === 0) return NO_FEED_SOURCES;
  return evidenceReason(input, [], sources);
}

const TARGET_SYMBOL: Record<PdpTarget["op"], string> = { ">=": "≥", "<=": "≤", ">": ">", "<": "<" };
const targetText = (t: PdpTarget): string => `${TARGET_SYMBOL[t.op]} ${t.value} ${t.unit}`;

/* a form-level refusal is brought to the reader: focusable alert (the
   PdpView idiom) */
function FormError({ error, nonce }: { error: string | null; nonce: number }): JSX.Element {
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (error) ref.current?.focus();
  }, [error, nonce]);
  return (
    <p className="form-error" role="alert" tabIndex={-1} ref={ref}>
      {error ?? ""}
    </p>
  );
}

interface Draft {
  kind: Kind;
  url: string;
  title: string;
  sourceId: string;
  measure: string;
  criterionId: string;
  unconfirmed: boolean;
  why: string;
  percent: string;
  note: string;
}

const draftFrom = (current: EvidenceValue | null): Draft => {
  const blank: Draft = {
    kind: "link",
    url: "",
    title: "",
    sourceId: "",
    measure: "",
    criterionId: "",
    unconfirmed: false,
    why: "",
    percent: "",
    note: "",
  };
  if (!current) return blank;
  if (current.kind === "link")
    return { ...blank, kind: "link", url: current.url, title: current.title, unconfirmed: current.unconfirmed, why: current.why ?? "" };
  if (current.kind === "feed")
    return {
      ...blank,
      kind: "feed",
      sourceId: current.ref.sourceId,
      measure: current.ref.measure,
      criterionId: current.criterionId ?? "",
      unconfirmed: current.unconfirmed,
      why: current.why ?? "",
    };
  return { ...blank, kind: "estimate", percent: String(current.percent), note: current.note };
};

const inputFrom = (d: Draft): EvidenceInput => {
  const why = d.unconfirmed ? { why: d.why.trim() } : {};
  if (d.kind === "link") return { kind: "link", url: d.url.trim(), title: d.title.trim(), unconfirmed: d.unconfirmed, ...why };
  if (d.kind === "feed")
    return {
      kind: "feed",
      ref: { sourceId: d.sourceId, measure: d.measure.trim() },
      criterionId: d.criterionId || null,
      unconfirmed: d.unconfirmed,
      ...why,
    };
  return { kind: "estimate", percent: d.percent.trim() === "" ? NaN : Number(d.percent), note: d.note.trim() };
};

export function EvidenceForm({ slot, current, feedSources, criteria, error, errorNonce = 0, saving, onSave, onClear, onCancel }: EvidenceFormProps): JSX.Element {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(current));
  const [localError, setLocalError] = useState<string | null>(null);
  const [localRefusals, setLocalRefusals] = useState(0);
  const [measureError, setMeasureError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => {
    first.current?.focus();
  }, []);

  const patch = (p: Partial<Draft>): void => {
    setDraft((d) => ({ ...d, ...p }));
    setLocalError(null);
  };
  const targeted = criteria.filter((c) => c.phase === slot.phase && c.target !== null);
  const chosenCriterion = targeted.find((c) => c.id === draft.criterionId);
  const feedRefused = draft.kind === "feed" && feedSources.length === 0;
  const pct = Number(draft.percent);
  const estimateInline = draft.kind === "estimate" && draft.percent.trim() !== "" && pct > ESTIMATE_MAX ? ESTIMATE_CAP_REFUSAL : "";

  const submit = (): void => {
    const input = inputFrom(draft);
    const refused = evidenceFormReason(input, feedSources);
    if (refused) {
      setLocalError(refused);
      setLocalRefusals((n) => n + 1);
      return;
    }
    onSave(input);
  };
  const clear = (): void => {
    if (window.confirm(`Clear the recorded evidence for ${slot.name}? It stays in the row's history.`)) onClear();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLFormElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  /* the checked kind carries the focus ref — a Replace… on an estimate or a
     feed opens on that segment, the one in the tab order */
  const kindRadio = (kind: Kind, id: string, label: string): JSX.Element => (
    <span className="seg-item" key={id}>
      <input type="radio" name="kind" id={id} ref={draft.kind === kind ? first : undefined} checked={draft.kind === kind} onChange={() => patch({ kind })} />
      <label htmlFor={id}>{label}</label>
    </span>
  );

  return (
    <form
      id="evidence-form"
      className="card-form evidence-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      onKeyDown={onKeyDown}
    >
      <h3 id="evidence-title">{slot.name}</h3>
      <p className="slot-note">
        {slot.swimlane} → {slot.path} · owed in Phase {slot.phase} · Owner: {slot.ownerRoleNames.join(" / ")} · Reviewer:{" "}
        {slot.reviewerRoleNames.join(" / ")}
      </p>

      <fieldset className="seg">
        <legend>Kind</legend>
        {kindRadio("link", "kind-link", "Link to artifact")}
        {kindRadio("feed", "kind-feed", "Data feed")}
        {kindRadio("estimate", "kind-estimate", "Estimate only")}
      </fieldset>

      {draft.kind === "link" ? (
        <>
          <label className="field" htmlFor="evidence-url">
            <span className="field-label">URL</span>
            <input type="url" id="evidence-url" value={draft.url} maxLength={URL_MAX} onChange={(e) => patch({ url: e.target.value })} />
          </label>
          <label className="field" htmlFor="evidence-title-input">
            <span className="field-label">Title, as the row will show it</span>
            <input type="text" id="evidence-title-input" value={draft.title} maxLength={TITLE_MAX} onChange={(e) => patch({ title: e.target.value })} />
          </label>
        </>
      ) : null}

      {draft.kind === "feed" ? (
        feedRefused ? (
          <p className="field-hint">{NO_FEED_SOURCES} — record a link instead.</p>
        ) : (
          <>
            <label className="field" htmlFor="feed-source">
              <span className="field-label">Source</span>
              <select id="feed-source" value={draft.sourceId} onChange={(e) => patch({ sourceId: e.target.value })}>
                <option value="">— pick a source —</option>
                {feedSources.map((s) => (
                  <option key={s.id} value={s.id} data-kind={s.kind}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field" htmlFor="feed-measure">
              <span className="field-label">Measure</span>
              <input
                type="text"
                id="feed-measure"
                className="rec-mono"
                value={draft.measure}
                onChange={(e) => {
                  patch({ measure: e.target.value });
                  setMeasureError(null);
                }}
                onBlur={() => setMeasureError(feedRefReason({ sourceId: draft.sourceId || "?", measure: draft.measure }))}
              />
              <span className="field-hint">
                The measure name as it exists in the source, e.g. <code>productivity_pct_of_manned</code>; letters, digits, space, _ % . / : -
              </span>
              <span className="form-error field-error" id="feed-measure-error">
                {measureError ?? ""}
              </span>
            </label>
            <label className="field" htmlFor="feed-criterion">
              <span className="field-label">Compare with exit criterion</span>
              <span className="field-hint">Met / not met is judged against the catalog target, never a number typed here.</span>
              <select id="feed-criterion" value={draft.criterionId} onChange={(e) => patch({ criterionId: e.target.value })}>
                <option value="">— none —</option>
                {targeted.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.area} — {c.text} ({targetText(c.target!)})
                  </option>
                ))}
              </select>
              <span className="field-hint" id="feed-criterion-hint">
                {chosenCriterion ? `Met when the reading is ${targetText(chosenCriterion.target!)} — pick a measure in that unit` : ""}
              </span>
            </label>
          </>
        )
      ) : null}

      {draft.kind === "estimate" ? (
        <>
          <label className="field" htmlFor="estimate-percent">
            <span className="field-label">Percent</span>
            <input
              type="number"
              id="estimate-percent"
              min={0}
              max={ESTIMATE_MAX}
              step={1}
              value={draft.percent}
              onChange={(e) => patch({ percent: e.target.value })}
            />
            <span className="field-hint" id="estimate-hint">
              0–90. Above 90, link the artifact instead.
            </span>
            <span className="form-error field-error" id="estimate-percent-error">
              {estimateInline}
            </span>
          </label>
          <label className="field" htmlFor="estimate-note">
            <span className="field-label">Note</span>
            <input type="text" id="estimate-note" value={draft.note} maxLength={NOTE_MAX} onChange={(e) => patch({ note: e.target.value })} />
          </label>
        </>
      ) : (
        <fieldset className="seg confidence">
          <legend>Confidence</legend>
          <span className="seg-item">
            <input type="radio" name="confidence" id="conf-confirmed" checked={!draft.unconfirmed} onChange={() => patch({ unconfirmed: false })} />
            <label htmlFor="conf-confirmed">Confirmed: this is the artifact that satisfies the row</label>
          </span>
          <span className="seg-item">
            <input type="radio" name="confidence" id="conf-unconfirmed" checked={draft.unconfirmed} onChange={() => patch({ unconfirmed: true })} />
            <label htmlFor="conf-unconfirmed">Unconfirmed, because…</label>
          </span>
          {draft.unconfirmed ? (
            <label className="field" htmlFor="evidence-why">
              <span className="sr-only">Why unconfirmed</span>
              <input type="text" id="evidence-why" value={draft.why} maxLength={NOTE_MAX} placeholder="reason" onChange={(e) => patch({ why: e.target.value })} />
            </label>
          ) : null}
        </fieldset>
      )}

      <FormError error={localError ?? error} nonce={localRefusals + errorNonce} />
      <div className="form-actions">
        <button type="submit" id="save-evidence" className="btn primary" disabled={saving || feedRefused}>
          Save evidence
        </button>
        {current ? (
          <button type="button" id="clear-evidence" className="btn ghost" onClick={clear}>
            Clear…
          </button>
        ) : null}
        <button type="button" id="cancel-evidence" className="btn ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
